import React from 'react';
import { Card } from '../../../components/ui/card';
import { Skeleton } from '../../../components/ui/skeleton';
import { StatusPill } from '../../../components/app';
import { cn } from '../../../lib/utils';
import { FounderCardsProps } from '../types';

/**
 * Основатель — полосой во всю ширину: их бывает двое-трое, и сетка карточек
 * оставляла бы половину экрана пустой. Слева имя и Доля в прибыли, справа путь денег:
 * вложил → возвращено → долг → прибыли получено → доступно взять.
 */
export function FounderCards({ founders, isLoading, formatMoney }: FounderCardsProps) {
  if (isLoading) {
    return (
      <Card className="flex flex-col gap-3 p-[18px]">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-8 w-full" />
      </Card>
    );
  }
  if (founders.length === 0) return null;

  const cell = (label: string, value: number, tone?: 'debt' | 'available') => (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-xs text-text-2">{label}</span>
      <b
        className={cn(
          'font-display text-lg font-bold tabular-nums',
          tone === 'debt' && value > 0 && 'text-warn',
          tone === 'available' && value > 0 && 'text-pos',
          tone === 'available' && value < 0 && 'text-neg',
        )}
      >
        {formatMoney(value)}
      </b>
    </div>
  );

  return (
    <section aria-label="Основатели" className="flex flex-col gap-3">
      {founders.map((founder) => (
        <Card key={founder.payerId} className="p-[18px]">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-8">
            <div className="flex shrink-0 flex-col items-start gap-1.5 lg:w-44">
              <h2 className="m-0 font-display text-[17px] font-bold">{founder.name}</h2>
              <StatusPill size="sm" tone="neutral">
                Доля в прибыли {founder.profitShare}%
              </StatusPill>
            </div>
            <div className="grid flex-1 grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-5">
              {cell('Вложил', founder.contributed)}
              {cell('Возвращено', founder.reimbursed)}
              {cell('Долг перед ним', founder.debt, 'debt')}
              {cell('Прибыли получено', founder.profitTaken)}
              {cell('Доступно взять', founder.profitAvailable, 'available')}
            </div>
          </div>
        </Card>
      ))}
    </section>
  );
}
